import React, { useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Grid2 as Grid,
  FormControlLabel,
  SxProps,
  Theme,
  useTheme,
  Tooltip,
  InputAdornment,
  TextField,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ExternalLink } from "../../components/ExternalLink";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { InvoiceInfoModel, InvoiceStatus } from "../../models/ChasableModels";
import { formatAmount } from "../../utils/formatAmount";
import { formatDate } from "../../utils/formatDate";
import { InvoiceStatusComponent } from "../../components/InvoiceStatus";
import { calculateOverdueColorHex } from "../../services/helpers";
import { StyledTable } from "./styled";
import { NewTag } from "../../components/NewTag";
import { ReceivableTag } from "../../components/ReceivableTag";
import { useNavigate } from "react-router-dom";
import TextEllipsisWithTooltip from "../../components/TextEllipsis";

type Props = {
  invoices: InvoiceInfoModel[];
  selectable?: boolean;
  disabled?: boolean;
  showTableOnly?: boolean;
  isPayable?: boolean;
  tableStyle?: SxProps<Theme>;
  tableFooter?: React.ReactNode;
  selectedInvoiceIds?: string[];
  fullHeight?: boolean;
  onSelectChange?(ids: string[]): void;
  onInvoiceClick?(e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, id: string): void;
  renderLink?: (invoice: InvoiceInfoModel) => React.ReactNode;
};

export function InvoiceSelect({
  invoices,
  selectable = false,
  disabled = false,
  showTableOnly = false,
  isPayable = false,
  tableStyle,
  tableFooter,
  selectedInvoiceIds,
  fullHeight = false,
  onSelectChange,
  onInvoiceClick,
  renderLink,
}: Props) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [search, setSearch] = useState("");
  const initialSelectedIdsRef = useRef<Set<string> | null>(null);
  if (initialSelectedIdsRef.current === null && selectedInvoiceIds !== undefined) {
    initialSelectedIdsRef.current = new Set(selectedInvoiceIds);
  }

  const isChasable = (invoice: InvoiceInfoModel) =>
    !isPayable && (invoice.status === InvoiceStatus.AUTHORISED || invoice.status === InvoiceStatus.SUBMITTED);

  const validInvoices = useMemo(
    () =>
      invoices
        .filter(({ status }) => {
          return status !== InvoiceStatus.DELETED && status !== InvoiceStatus.VOIDED;
        })
        .sort((invoiceA, invoiceB) => {
          const aChasable = isChasable(invoiceA);
          const bChasable = isChasable(invoiceB);
          if (aChasable !== bChasable) return aChasable ? -1 : 1;

          if (aChasable) {
            const aNeverChased = !invoiceA.lastCompleted;
            const bNeverChased = !invoiceB.lastCompleted;
            if (aNeverChased !== bNeverChased) return aNeverChased ? -1 : 1;

            const dueDiff = new Date(invoiceA.dueDate).getTime() - new Date(invoiceB.dueDate).getTime();
            if (dueDiff !== 0) return dueDiff;

            return invoiceA.invoiceNumber.localeCompare(invoiceB.invoiceNumber);
          }

          return 0;
        }),
    [invoices]
  );

  const filteredInvoices = useMemo(() => {
    if (!search.trim()) return validInvoices;
    const q = search.trim().toLowerCase();
    return validInvoices.filter((inv) => {
      const amountStr = formatAmount(inv.amount, inv.currency).toLowerCase();
      const dueStr = formatDate(inv.dueDate).toLowerCase();
      return (
        inv.invoiceNumber.toLowerCase().includes(q) ||
        inv.currency.toLowerCase().includes(q) ||
        amountStr.includes(q) ||
        dueStr.includes(q)
      );
    });
  }, [validInvoices, search]);

  const invoicesForDisplay = useMemo(() => {
    const initialSet = initialSelectedIdsRef.current;
    if (!initialSet?.size) return filteredInvoices;
    const selected: InvoiceInfoModel[] = [];
    const unselected: InvoiceInfoModel[] = [];
    for (const inv of filteredInvoices) {
      if (initialSet.has(inv.id)) selected.push(inv);
      else unselected.push(inv);
    }
    return [...selected, ...unselected];
  }, [filteredInvoices]);

  const selectableInvoices = filteredInvoices.filter(isChasable);
  const selectableInvoiceCount = selectableInvoices.length;

  const onCheckedChange = (invoiceId: string, checked: boolean) => {
    const ids = checked
      ? [...(selectedInvoiceIds ?? []), invoiceId]
      : (selectedInvoiceIds ?? []).filter((id) => id !== invoiceId);
    const orderedIds = validInvoices.map(({ id }) => id).filter((id) => ids.includes(id));
    onSelectChange?.(orderedIds);
  };

  const isAllChecked = (selectedInvoiceIds ?? []).length === selectableInvoiceCount;
  const isIndeterminate =
    (selectedInvoiceIds ?? []).length > 0 && (selectedInvoiceIds ?? []).length < selectableInvoiceCount;

  const onToggleAll = () => {
    if (isAllChecked) {
      onSelectChange?.([]);
    } else {
      onSelectChange?.(selectableInvoices.map(({ id }) => id));
    }
  };

  const navigateToChasable = (event: React.MouseEvent<HTMLElement, MouseEvent>, invoiceId: string) => {
    event.preventDefault();
    if (invoiceId) {
      navigate(`/chasables/suggested/${invoiceId}?isFromSearch=true&showBackButton=true`);
    }
  };

  const renderTable = () => (
    <Box sx={tableStyle}>
      {!selectable && (
        <TextField
          size="small"
          placeholder="Filter invoices..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            mb: 0.5,
            "& .MuiOutlinedInput-root": { backgroundColor: isDarkMode ? "grey.900" : "grey.50" },
          }}
          aria-label="Filter invoices"
        />
      )}
      <TableContainer
        sx={{
          backgroundColor: isDarkMode ? "common.black" : "common.white",
          maxHeight: fullHeight ? "calc(100vh - 360px)" : "300px",
          overflowY: "auto",
        }}
      >
        <StyledTable stickyHeader size="small" aria-label="invoice table">
          <TableHead>
            <TableRow>
              <TableCell>
                {selectable ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAllChecked}
                        indeterminate={isIndeterminate}
                        disabled={disabled || !selectableInvoiceCount}
                        onChange={onToggleAll}
                        sx={{ "& .MuiSvgIcon-root": { fontSize: 24 } }}
                      />
                    }
                    label={<Typography fontWeight={700}>Invoice</Typography>}
                    sx={{ ml: 0, userSelect: "none" }}
                  />
                ) : (
                  "Invoice"
                )}
              </TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInvoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {search.trim() ? "No invoices match your filter." : "No invoices."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              invoicesForDisplay.map((invoice) => (
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    invoice.lastCompleted ? (
                      <Typography variant="body2">Last chased on {formatDate(invoice.lastCompleted)}</Typography>
                    ) : undefined
                  }
                >
                  <TableRow
                    key={invoice.id}
                    onClick={(e) =>
                      isChasable(invoice) && !onInvoiceClick
                        ? navigateToChasable(e, invoice.id)
                        : onInvoiceClick?.(e, invoice.id)
                    }
                    sx={{ cursor: onInvoiceClick ? "pointer" : "default", "&:hover": { backgroundColor: "grey.100" } }}
                  >
                    <TableCell>
                      <Grid container alignItems="center">
                        {selectable && (
                          <Grid
                            onClick={(e) => e.stopPropagation()}
                            sx={{ cursor: disabled || !isChasable(invoice) ? "not-allowed" : "pointer" }}
                          >
                            <Checkbox
                              checked={(selectedInvoiceIds ?? []).includes(invoice.id)}
                              disabled={disabled || !isChasable(invoice)}
                              onChange={(_, checked) => onCheckedChange(invoice.id, checked)}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 18, padding: 0 }, padding: 0, mr: "5px" }}
                            />
                          </Grid>
                        )}
                        <Grid>
                          {renderLink?.(invoice) ?? (
                            <ExternalLink href={invoice.link}>
                              <TextEllipsisWithTooltip
                                text={invoice.invoiceNumber}
                                maxLength={15}
                                sx={{ fontSize: "12px" }}
                              />
                            </ExternalLink>
                          )}
                          <Typography>{formatAmount(invoice.amount, invoice.currency)}</Typography>
                          {invoice.dispute && (
                            <Tooltip
                              title={
                                <Typography variant="body2">
                                  {invoice.dispute.reasonCode}: {invoice.dispute.reason}
                                </Typography>
                              }
                              arrow
                            >
                              <Chip
                                size="small"
                                color="error"
                                label="Disputed"
                                sx={{
                                  borderRadius: "4px",
                                  p: 0,
                                  height: "unset",
                                  fontSize: "11px",
                                  "& .MuiChip-label": { px: "4px", py: "0" },
                                }}
                              />
                            </Tooltip>
                          )}
                        </Grid>
                      </Grid>
                    </TableCell>
                    {/* !important is used to override the default padding of the table cell */}
                    <TableCell align="right" sx={{ paddingRight: "4px!important" }}>
                      {isChasable(invoice) ? <ReceivableTag /> : invoice.actionType === "NewInvoice" && <NewTag />}

                      <Typography>Due {formatDate(invoice.dueDate)}</Typography>
                      <InvoiceStatusComponent
                        color={calculateOverdueColorHex(new Date(invoice.dueDate))}
                        invoice={invoice}
                      />
                    </TableCell>
                  </TableRow>
                </Tooltip>
              ))
            )}
          </TableBody>
        </StyledTable>
      </TableContainer>
      {tableFooter && <Box>{tableFooter}</Box>}
    </Box>
  );

  if (showTableOnly) {
    return renderTable();
  }

  return (
    <Accordion defaultExpanded disableGutters>
      <AccordionSummary
        expandIcon={<KeyboardArrowDownIcon />}
        aria-controls="invoices-panel-content"
        id="invoices-panel-header"
      >
        <Typography variant="h3">Invoices</Typography>
      </AccordionSummary>
      <AccordionDetails>{renderTable()}</AccordionDetails>
    </Accordion>
  );
}
